import { createBrowserRouter, Outlet, RouteObject } from "react-router-dom";

import {
  LOGIN_ERROR_ROUTE,
  LOGIN_ROUTE,
  OVERVIEW_ROUTE,
  OVERVIEW_ROUTE_SEGMENT,
  OVERVIEW_ROUTE_CHARTS_SEGMENT,
  OVERVIEW_ROUTE_CHARTS_INTERVENTION_SEGMENT,
  OVERVIEW_ROUTE_CHARTS_OPERATIONAL_SEGMENT,
  OVERVIEW_ROUTE_CHARTS_OPERATIONAL,
  OVERVIEW_ROUTE_MAP_SEGMENT,
  OVERVIEW_ROUTE_MAP_HYBRID_SEGMENT,
  OVERVIEW_ROUTE_MAP_HYBRID,
  OVERVIEW_ROUTE_MAP_SATELLITE_SEGMENT,
  OVERVIEW_ROUTE_TABLES_SEGMENT,
  OVERVIEW_ROUTE_TABLES_KCF_SEGMENT,
  OVERVIEW_ROUTE_TABLES_KLF_SEGMENT,
  OVERVIEW_ROUTE_TABLES_RISK_SEGMENT,
  OVERVIEW_ROUTE_TABLES_WELL_SEGMENT,
  OVERVIEW_ROUTE_TABLES_WELL,
  RELOG_ROUTE,
  RISK_ROUTE,
  RISK_ROUTE_CHARTS_MAJOR_SEGMENT,
  RISK_ROUTE_CHARTS_OVERALL_SEGMENT,
  RISK_ROUTE_CHARTS_SEGMENT,
  RISK_ROUTE_SEGMENT,
  RISK_ROUTE_TABLES_SEGMENT,
  ROUTE_HEADINGS,
  WELL_ID_NAME_PARAM,
  LOGOUT_ROUTE,
  EXPORT_ROUTE,
  EXPORT_ROUTE_SEGMENT,
  CONFIG_ROUTE_SEGMENT,
  CONFIG_LH_ROUTE,
  CONFIG_LH_ROUTE_SEGMENT,
  CONFIG_IMPORT_ROUTE,
  CONFIG_IMPORT_DATA_ROUTE_SEGMENT,
  FAILURE_MODE_EFFECT_PARAM,
  CONFIG_ATTRIBUTE_MAPPING_ROUTE_SEGMENT,
  CONFIG_DISPLAY_OPTIONS_ROUTE_SEGMENT,
  RISK_ROUTE_CHARTS_MITIGATION_SEGMENT,
  RISK_ROUTE_CHARTS,
} from "constants/routing";
import { NotFound404 } from "components/error/NotFound404";
import { GatewayFallback } from "components/error/GatewayFallback";
import { LoginError } from "components/error/LoginError";
import { ExportProvider } from "contexts/export/ExportProvider";
import { makeRedirectTo, redirectToDefault } from "helpers/routing";
import { ExportLayout } from "layouts/ExportLayout";
import { RiskManagementLayout } from "layouts/RiskLayout";

import {
  overviewPrimaryTabs,
  overviewSecondaryChartsTabs,
  overviewSecondaryMapTabs,
  overviewSecondaryTablesTabs,
  configLhTabs,
  riskPrimaryTabs,
  riskSecondaryChartsTabs,
} from "./handles";
import { App } from "./App";
import { Home } from "./Home";
import { Login } from "./Login";
import { Logout } from "./Logout";
import {
  SystemOverviewKCF,
  SystemOverviewKLF,
  SystemOverviewOperationalCharts,
  SystemOverviewRiskSummaries,
  SystemOverviewInterventionCharts,
  SystemOverviewWellAttributes,
  RiskManagementDataTable,
  RiskManagementOverallRiskCharts,
  RiskManagementMajorRiskCharts,
  RiskManagementMitigationRiskCharts,
  SystemOverviewHybridMap,
  SystemOverviewSatelliteMap,
  RiskManagementCharts,
} from "./outlets";
import { ExportPage } from "./ExportPage";
import { loadConfigurationRouteModules } from "./lazy.ts";
import { SystemOverviewPage } from "./SystemOverviewPage";

export const routes = [
  {
    path: "/",
    Component: App,
    HydrateFallback: () => null,
    children: [
      {
        path: "/",
        Component: Home,
        children: [
          {
            index: true,
            loader: redirectToDefault,
            Component: Outlet,
          },
          {
            path: OVERVIEW_ROUTE_SEGMENT,
            handle: {
              navTabs: {
                primaryTabs: overviewPrimaryTabs,
              },
              header: ROUTE_HEADINGS[OVERVIEW_ROUTE],
            },
            Component: SystemOverviewPage,
            children: [
              {
                index: true,
                loader: redirectToDefault,
                Component: Outlet,
              },
              {
                path: OVERVIEW_ROUTE_CHARTS_SEGMENT,
                handle: {
                  navTabs: {
                    secondaryTabs: overviewSecondaryChartsTabs,
                  },
                },
                Component: Outlet,
                children: [
                  {
                    index: true,
                    loader: makeRedirectTo(OVERVIEW_ROUTE_CHARTS_OPERATIONAL),
                    Component: Outlet,
                  },
                  {
                    handle: {
                      yAxisLabel: "Operational Risk",
                    },
                    path: OVERVIEW_ROUTE_CHARTS_OPERATIONAL_SEGMENT,
                    Component: SystemOverviewOperationalCharts,
                  },
                  {
                    handle: {
                      yAxisLabel: "Risk",
                    },
                    path: OVERVIEW_ROUTE_CHARTS_INTERVENTION_SEGMENT,
                    Component: SystemOverviewInterventionCharts,
                  },
                ],
              },
              {
                path: OVERVIEW_ROUTE_TABLES_SEGMENT,
                handle: {
                  navTabs: {
                    secondaryTabs: overviewSecondaryTablesTabs,
                  },
                },
                Component: Outlet,
                children: [
                  {
                    index: true,
                    loader: makeRedirectTo(OVERVIEW_ROUTE_TABLES_WELL),
                    Component: Outlet,
                  },
                  {
                    path: OVERVIEW_ROUTE_TABLES_WELL_SEGMENT,
                    Component: SystemOverviewWellAttributes,
                  },
                  {
                    path: OVERVIEW_ROUTE_TABLES_RISK_SEGMENT,
                    Component: SystemOverviewRiskSummaries,
                  },
                  {
                    path: OVERVIEW_ROUTE_TABLES_KLF_SEGMENT,
                    Component: SystemOverviewKLF,
                  },
                  {
                    path: OVERVIEW_ROUTE_TABLES_KCF_SEGMENT,
                    Component: SystemOverviewKCF,
                  },
                ],
              },
              {
                path: OVERVIEW_ROUTE_MAP_SEGMENT,
                handle: {
                  navTabs: {
                    secondaryTabs: overviewSecondaryMapTabs,
                  },
                },
                Component: Outlet,
                children: [
                  {
                    index: true,
                    loader: makeRedirectTo(OVERVIEW_ROUTE_MAP_HYBRID),
                    Component: Outlet,
                  },
                  {
                    path: OVERVIEW_ROUTE_MAP_HYBRID_SEGMENT,
                    Component: SystemOverviewHybridMap,
                  },
                  {
                    path: OVERVIEW_ROUTE_MAP_SATELLITE_SEGMENT,
                    Component: SystemOverviewSatelliteMap,
                  },
                ],
              },
            ],
          },
          {
            path: RISK_ROUTE_SEGMENT,
            handle: {
              navTabs: {
                primaryTabs: riskPrimaryTabs,
              },
              header: ROUTE_HEADINGS[RISK_ROUTE],
            },
            Component: RiskManagementLayout,
            children: [
              {
                index: true,
                loader: makeRedirectTo(RISK_ROUTE_CHARTS),
                Component: Outlet,
              },
              {
                path: RISK_ROUTE_CHARTS_SEGMENT,
                handle: {
                  navTabs: {
                    secondaryTabs: riskSecondaryChartsTabs,
                  },
                  sidebar: {
                    items: [
                      "SidebarMiniHeading",
                      "RiskMitigationSelect",
                      "RiskAssetSelection",
                    ],
                    hasWellSelection: true,
                  },
                },
                Component: RiskManagementCharts,
                children: [
                  {
                    handle: {
                      yAxisLabel: "Overall Risk",
                    },
                    path: RISK_ROUTE_CHARTS_OVERALL_SEGMENT,
                    Component: Outlet,
                    children: [
                      {
                        index: true,
                        Component: RiskManagementOverallRiskCharts,
                      },
                      {
                        path: `:${WELL_ID_NAME_PARAM}`,
                        Component: RiskManagementOverallRiskCharts,
                      },
                    ],
                  },
                  {
                    handle: {
                      yAxisLabel: "Major Event Risk",
                    },
                    path: RISK_ROUTE_CHARTS_MAJOR_SEGMENT,
                    Component: Outlet,
                    children: [
                      {
                        index: true,
                        Component: RiskManagementMajorRiskCharts,
                      },
                      {
                        path: `:${WELL_ID_NAME_PARAM}`,
                        Component: RiskManagementMajorRiskCharts,
                      },
                    ],
                  },
                  {
                    handle: {
                      yAxisLabel: "Mitigation Risk",
                    },
                    path: RISK_ROUTE_CHARTS_MITIGATION_SEGMENT,
                    Component: Outlet,
                    children: [
                      {
                        index: true,
                        Component: RiskManagementMitigationRiskCharts,
                      },
                      {
                        path: `:${WELL_ID_NAME_PARAM}`,
                        Component: RiskManagementMitigationRiskCharts,
                      },
                    ],
                  },
                ],
              },
              {
                path: RISK_ROUTE_TABLES_SEGMENT,
                handle: {
                  sidebar: {
                    items: ["RiskAssetSelection", "OutcomeScenariosSelect"],
                  },
                },
                Component: RiskManagementDataTable,
                children: [
                  {
                    index: true,
                    Component: RiskManagementDataTable,
                  },
                  {
                    path: `:${FAILURE_MODE_EFFECT_PARAM}`,
                    Component: RiskManagementDataTable,
                  },
                ],
              },
            ],
          },
          {
            path: EXPORT_ROUTE_SEGMENT,
            handle: {
              header: ROUTE_HEADINGS[EXPORT_ROUTE],
            },
            Component: () => (
              <ExportProvider>
                <ExportLayout />
              </ExportProvider>
            ),
            children: [
              {
                index: true,
                Component: ExportPage,
              },
            ],
          },
          {
            path: CONFIG_ROUTE_SEGMENT,
            lazy: async () => {
              const { ConfigurationPage } =
                await loadConfigurationRouteModules();

              return {
                Component: ConfigurationPage,
              };
            },
            children: [
              {
                index: true,
                loader: makeRedirectTo(CONFIG_IMPORT_ROUTE),
                Component: Outlet,
              },
              {
                path: CONFIG_LH_ROUTE_SEGMENT,
                handle: {
                  header: ROUTE_HEADINGS[CONFIG_LH_ROUTE],
                  navTabs: {
                    primaryTabs: configLhTabs,
                  },
                },
                Component: Outlet,
                children: [
                  {
                    index: true,
                    loader: makeRedirectTo(CONFIG_IMPORT_ROUTE),
                    Component: Outlet,
                  },
                  {
                    path: CONFIG_IMPORT_DATA_ROUTE_SEGMENT,
                    lazy: async () => {
                      const { ImportData } =
                        await loadConfigurationRouteModules();

                      return {
                        Component: ImportData,
                      };
                    },
                  },
                  {
                    path: CONFIG_ATTRIBUTE_MAPPING_ROUTE_SEGMENT,
                    lazy: async () => {
                      const { AttributeMapping } =
                        await loadConfigurationRouteModules();

                      return {
                        Component: AttributeMapping,
                      };
                    },
                  },
                  {
                    path: CONFIG_DISPLAY_OPTIONS_ROUTE_SEGMENT,
                    lazy: async () => {
                      const { DisplayOptionsOutlet } =
                        await loadConfigurationRouteModules();

                      return {
                        Component: DisplayOptionsOutlet,
                      };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: LOGIN_ROUTE,
        Component: Login,
      },
      {
        path: LOGOUT_ROUTE,
        Component: Logout,
      },
      {
        path: LOGIN_ERROR_ROUTE,
        Component: LoginError,
      },
      {
        path: "*",
        Component: NotFound404,
      },
    ],
  },
  {
    path: RELOG_ROUTE,
    Component: GatewayFallback,
  },
] satisfies Array<RouteObject>;

export const router = createBrowserRouter(routes, {
  future: {
    v7_fetcherPersist: true,
    v7_relativeSplatPath: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});

export default router;
